import { useCallback } from 'react'
import { useCurrentRate } from './hooks/useCurrentRate'
import { useHebcal } from './hooks/useHebcal'
import { useLanguage } from './i18n/LanguageContext'
import { translations } from './i18n/translations'
import { AppShell } from './components/layout/AppShell'
import { Header } from './components/layout/Header'
import { Footer } from './components/layout/Footer'
import { CollapsibleSection } from './components/layout/CollapsibleSection'
import { HeroSection } from './components/hero/HeroSection'
import { DailyTimeline } from './components/timeline/DailyTimeline'
import { ApplianceCalculator } from './components/calculator/ApplianceCalculator'
import { ShabbatHoliday, getShabbatPreview } from './components/shabbat/ShabbatHoliday'
import { SavingsTips, getMaxTipSaving } from './components/tips/SavingsTips'
import { DonateCard } from './components/donate/DonateCard'
import { ShareButton } from './components/share/ShareButton'

export default function App() {
  const rate = useCurrentRate()
  const { shabbat } = useHebcal()
  const { lang, t } = useLanguage()
  const handleSeeMore = useCallback(() => {
    const el = document.getElementById('calculator-section')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      // Expand the section by clicking its button
      const btn = el.querySelector('button')
      if (btn) btn.click()
    }
  }, [])

  const maxTipSaving = getMaxTipSaving()
  const tipPreviewFn = translations.tips.preview[lang]
  const tipPreview = tipPreviewFn(maxTipSaving)
  const shabbatPreview = getShabbatPreview(shabbat.candleLighting, lang)

  return (
    <AppShell tier={rate.tier}>
      <Header tier={rate.tier} season={rate.season} />

      {/* Zone A — The Glance */}
      <HeroSection rate={rate} onSeeMore={handleSeeMore} />

      {/* Zone B — The Schedule */}
      <div className="mt-4">
        <DailyTimeline />
      </div>

      {/* Zone C — On-Demand (collapsed) */}
      <div className="mt-4 space-y-3">
        <CollapsibleSection
          id="calculator-section"
          title={t('collapsible.calculator') as string}
          preview={t('calculator.preview') as string}
        >
          <ApplianceCalculator tier={rate.tier} season={rate.season} />
        </CollapsibleSection>

        <CollapsibleSection
          title={t('collapsible.tips') as string}
          preview={tipPreview}
        >
          <SavingsTips />
        </CollapsibleSection>

        <CollapsibleSection
          title={t('collapsible.shabbat') as string}
          preview={shabbatPreview}
        >
          <ShabbatHoliday />
        </CollapsibleSection>
      </div>

      {/* Zone D — Support & Share */}
      <div className="mt-4 space-y-3">
        <DonateCard />
        <div className="text-center py-2">
          <ShareButton rate={rate} />
        </div>
      </div>

      <Footer />
    </AppShell>
  )
}
